import {
  UploadIcon,
  CheckCircledIcon,
  DownloadIcon,
} from "@radix-ui/react-icons";
import {
  Heading,
  Flex,
  Card,
  Button,
  Text,
  Badge,
  Box,
} from "@radix-ui/themes";
import { Controller, useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import {
  UploadModel,
  UploadModelItemProps,
  UploadModelItemType,
} from "../../types/ModelsType";
import { Models } from "../../utils/constants";
import { getBlob, postFormData } from "../../utils/axios-config";
import { useExportDocument } from "../../hooks/useExportDocument";
import SelectComponent from "../../components/inputs/Select/SelectComponent";

const DocumentModelsOptions = [
  { value: "certificado-licenciatura", label: "Certificado Licenciatura" },
  { value: "cartao-estudante", label: "Cartão de Estudante" },
  { value: "redefinicao-senha", label: "Redefinição de Senha" },
  { value: "pauta-exame", label: "Pauta de exame" },
  { value: "pauta-frequencia", label: "Pauta de Frequência" },
  { value: "disciplinas-estudante", label: "Disciplinas do Estudante" },
  { value: "ficha-matricula", label: "Ficha de Matricula" },
  { value: "inscricao-semestral", label: "Ficha de inscrição Semestral" },
];

const getLabelByValue = (value: string) =>
  DocumentModelsOptions.find((opt) => opt.value === value)?.label ?? value;

const DocumentVariablesMap: Record<
  string,
  { label: string; description?: string }[]
> = {
  "cartao-estudante": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{ies}", description: "Nome da IES" },
    { label: "{ano_lectivo}", description: "Ano letivo" },
    { label: "{nome_completo}", description: "Nome completo do estudante" },
    { label: "{curso}", description: "Curso" },
    { label: "{regime}", description: "Regime (diurno, pós-laboral, etc)" },
    { label: "{codigo}", description: "Código do estudante" },
    { label: "{nue}", description: "Número Único de Estudante" },
    { label: "{avatar}", description: "Foto do estudante" },
  ],
  "redefinicao-senha": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{info}", description: "Informações adicionais" },
    { label: "{nome_completo}", description: "Nome completo" },
    { label: "{documento_identificacao}", description: "BI ou Passaporte" },
    { label: "{senha}", description: "Nova senha gerada" },
    { label: "{utilizador}", description: "Nome de utilizador" },
  ],
  "disciplinas-estudante": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{info}", description: "Informações gerais" },
    { label: "{student_classes}", description: "Lista de disciplinas" },
    { label: "{ordem_lasses}", description: "Ordem das disciplinas" },
    { label: "{codigo}", description: "Código do estudante" },
    { label: "{nome}", description: "Primeiro nome" },
    { label: "{apelido}", description: "Apelido" },
    { label: "{cargo}", description: "Cargo (ex: estudante)" },
    { label: "{nome_completo}", description: "Nome completo" },
  ],
  "pauta-exame": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{cabecalho}", description: "Cabeçalho personalizado" },
    { label: "{Header}", description: "Cabeçalho da pauta" },
    { label: "{Footer}", description: "Rodapé da pauta" },
    { label: "{nome_completo}", description: "Nome do responsável" },
    { label: "{cargo}", description: "Cargoão do responsável" },
    { label: "{data}", description: "Data do exame" },
  ],
  "pauta-frequencia": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{cabecalho}", description: "Cabeçalho personalizado" },
    { label: "{Header}", description: "Cabeçalho da pauta" },
    { label: "{Footer}", description: "Rodapé da pauta" },
    { label: "{nome_completo}", description: "Nome do responsável" },
    { label: "{cargo}", description: "Cargoão do responsável" },
    { label: "{data}", description: "Data do exame" },
  ],
  "certificado-licenciatura": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{ies}", description: "Nome da IES" },
    { label: "{nome_completo}", description: "Nome completo do estudante" },
    { label: "{cargo}", description: "Cargo do reitor/diretor" },
    { label: "{estudante}", description: "Nome do estudante (redundante)" },
    { label: "{documento_de_identificacao}", description: "BI/Passaporte" },
    { label: "{pai}", description: "Nome do pai" },
    { label: "{mae}", description: "Nome da mãe" },
    { label: "{curso}", description: "Curso concluído" },
    {
      label: "{ano_lectivo_disciplina}",
      description: "Ano letivo da disciplina",
    },
    { label: "{nivel_semestre}", description: "Nível e semestre" },
    { label: "{disciplina}", description: "Nome da disciplina" },
    { label: "{resultado}", description: "Nota final" },
    { label: "{local}", description: "Local de emissão" },
    { label: "{emitido}", description: "Data de emissão" },
    { label: "{cargo}", description: "Cargo do emissor" },
  ],
  "ficha-matricula": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{ies}", description: "Nome da IES" },
    { label: "{data_emissao}", description: "Data de emissão" },
    { label: "{ano_lectivo}", description: "Ano letivo" },
    { label: "{nome_completo}", description: "Nome completo do estudante" },
    { label: "{codigo}", description: "Código do estudante" },
    { label: "{curso}", description: "Curso" },
    { label: "{local}", description: "Local de emissão" },
    { label: "{delegacao}", description: "Unidade da instituição onde o estudante está matriculado" },
    { label: "{cargo}", description: "Cargoão do responsável" },
  ],    
  "inscricao-semestral": [
    { label: "{logo}", description: "Logotipo da instituição" },
    { label: "{ies}", description: "Nome da IES" },
    { label: "{data_emissao}", description: "Data de emissão" },
    { label: "{ano_lectivo}", description: "Ano letivo" },
    { label: "{nome_completo}", description: "Nome completo do estudante" },
    { label: "{codigo}", description: "Código do estudante" },
    { label: "{curso}", description: "Curso" },
    { label: "{nivel}", description: "Nível de inscrição" },
    { label: "{semestre}", description: "Semestre de inscrição" },
    { label: "{local}", description: "Local de emissão" },
    { label: "{delegacao}", description: "Unidade da instituição onde o estudante está inscrito" },
    { label: "{cargo}", description: "Cargoão do responsável" },
    {label: "{nome_disciplina}", description: "Nome da disciplina" },
    {label: "{ordem}", description: "Ordem da disciplina" },
  ],
};

export const UploadModels = ({
  type = "cartao-estudante",
  existingFileName,
  onUploadSuccess,
}: UploadModelItemProps) => {
  const [isUploaded, setIsUploaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { downloadOnly } = useExportDocument();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<UploadModelItemType>({
    defaultValues: {
      type,
      file: null,
      existingFile: false,
    },
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
  });

  const file = watch("file");
  const selectedType = watch("type");
  const hasFile = !!file || !!existingFileName;

  useEffect(() => {
      setIsUploaded(!!existingFileName);
    setValue("existingFile", !!existingFileName);
  }, [existingFileName, setValue]);

  const handleFileSelect = (file?: File) => {
    if (!file) return;
    setValue("file", file);
    setIsUploaded(false);
  };

  const handleDownload = async () => {
    if (!selectedType ) return;
    try {
      const blob = await getBlob(`${Models}/${selectedType}`);
      const fileName = `${selectedType}.docx`;
      await downloadOnly(blob, fileName);
      toast.success("Download iniciado com sucesso");
    } catch (error: any) {
      console.error("Erro ao fazer download:", error);
      toast.error("Erro ao baixar o arquivo");
    } 
  };

  const onSubmit = handleSubmit(async (data) => {
    if (!data.file) {
      toast.error("Por favor, selecione um arquivo");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", data.file);
      formData.append("documentType", data.type);

      const response = await postFormData<UploadModel>(Models, formData);
      

       setValue("uploaded", true);
      toast.success("Modelo carregado com sucesso!");

       if (onUploadSuccess && response) {
        onUploadSuccess(response);
      }
      
      reset({ type: data.type, file: null, existingFile: true });
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      toast.error("Erro ao carregar modelo");
    } finally {
      setIsLoading(false);
    }
  });

  const currentLabel = getLabelByValue(selectedType);
  const variables = DocumentVariablesMap[selectedType] ?? [];

  return (
    <form onSubmit={onSubmit} noValidate>
      <Flex direction="column" gap="4">
        <Card
          style={{
            padding: "20px",
            marginBottom: "10px",
            border: isUploaded ? "2px solid #10b981" : "2px solid (--accent-9)",
            borderRadius: "var(--radius-3)" ,
          }}
        >
          <Flex direction="row" gap="6" align="start">
            <Box flexGrow="1" style={{ minWidth: "300px" }}>
              <Flex direction="row" gap="4" align="start">
                {/* <Box
                  style={{
                    width: "150px",
                    height: "150px",
                    border: "2px solid var(--accent-9)",
                    borderRadius: "var(--radius-3)" ,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundColor: "var(--gray-3)",
                    padding: "10px",
                  }}
                >
                  {hasFile ? (
                    <Flex direction="column" align="center" gap="2">
                      <div
                        style={{
                          fontSize: "48px",
                          color: "#2563eb",
                          lineHeight: 1,
                        }}
                      />
                      <Text
                        size="1"
                        weight="medium"
                        align="center"
                        style={{ wordBreak: "break-word" }}
                      >
                        {file?.name || existingFileName || "Documento .docx"}
                      </Text>
                      <Badge color={isUploaded ? "green" : "blue"} size="1">
                        {isUploaded ? "Carregado" : "Pronto para upload"}
                      </Badge>
                    </Flex>
                  ) : (
                    <Flex direction="column" align="center" gap="2">
                      <UploadIcon width="40" height="40" color="#ccc" />
                      <Text size="1" color="gray" align="center">
                        Nenhum ficheiro selecionado
                      </Text>
                    </Flex>
                  )}
                </Box> */}

                <Flex direction="column" gap="3" style={{ flex: 1 }}>
                  <Flex justify="between" align="center">
                    <Heading
                      size="4"
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        
                      }}
                    >
                      {currentLabel}
                    </Heading>
                    {isUploaded && (
                      <Badge color="green" size="1">
                        <CheckCircledIcon /> Carregado
                      </Badge>
                    )}
                  </Flex>

                  <Flex direction="column" gap="2">
                    <SelectComponent
                      required={true}
                      name="type"
                      label="Tipo de Documento"
                      control={control}
                      errors={errors}
                      options={DocumentModelsOptions}
                      style={{ width: "100%" }}
                    />
                  </Flex>

                  <Text size="2">
                    Formato aceite: .docx
                  </Text>

                  <Flex direction="column" gap="2">
                    <Controller
                      name="file"
                      control={control}
                      rules={{
                        required: !existingFileName
                          ? "Por favor, selecione um arquivo"
                          : false,
                      }}
                      render={({ field }) => (
                        <DragAndDropFileUpload
                          onFileDrop={(file) => {
                            field.onChange(file);
                            handleFileSelect(file!);
                          }}
                          label="Arraste ou selecione o ficheiro .docx"
                          selectedFile={field.value}
                          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          disabled={isLoading}
                        />
                      )}
                    />
                    {errors.file && (
                      <Text  size="1">
                        {errors.file.message}
                      </Text>
                    )}
                  </Flex>

                  <Flex gap="2" align="center" wrap="wrap">
                    <Button
                      type="submit"
                      disabled={!file || isLoading}
                      color="green"
                    >
                      {isLoading
                        ? "Processando..."
                        : existingFileName
                        ? "Atualizar Modelo"
                        : "Carregar Modelo"}
                    </Button>

                    
                {selectedType && ( 
                  <Button
                    type="button"
                    onClick={handleDownload}
                    disabled={isLoading}
                    variant="outline"
                  >
                    <DownloadIcon /> Descarregar
                  </Button>
                )}
                </Flex>

                   {existingFileName && (
                    <Text size="1" >
                      Último ficheiro: {existingFileName}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Box>

            <Box
              style={{
                minWidth: "320px",
                maxWidth: "380px",
                padding: "16px",
                backgroundColor: "var(--gray-3)",
                borderRadius: "var(--radius-3)" ,
                border: "1px solid(--accent-9)",
              }}
            >
              <Heading
                size="3"
                style={{ marginBottom: "12px"}}
              >
                Variáveis do Modelo
              </Heading>

              {variables.length > 0 ? (
                <Flex direction="column" gap="2">
                  {variables.map((item, idx) => (
                    <Flex
                      key={idx}
                      align="center"
                      gap="2"
                     
                    >
                      <Box
                        style={{
                          backgroundColor: "var(--gray-3)",
                          color: "#0369a1",
                          padding: "2px 6px",
                          borderRadius: "var(--radius-3)" ,
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        {item.label}
                      </Box>
                      <Text size="1" >
                        {item.description}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Text size="1" >
                  Selecione um tipo de documento para ver as variáveis.
                </Text>
              )}

              <Box
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  backgroundColor: "var(--gray-3)",
                  borderRadius: "var(--radius-3)" ,
                  border: "1px solid (--accent-9)",
                }}
              >
                <Text size="1" weight="medium" >
                  <strong>Nota:</strong> Estas são as variáveis a colocar no
                  modelo que o sistema irá substituir. Use o modo desenvolvedor
                  do Word (Office) para inserir os campos.
                </Text>
              </Box>
            </Box>
          </Flex>
        </Card>
      </Flex>
    </form>
  );
};
